import {DayAbbreviationType} from "@clover-web-lib-sprout/calendar";
import { DateTime } from "luxon";
import { HHMMtoDate, roundToNext15Minutes } from "./dateTimeUtils";
import { DayPart } from "@/dto/dayPartMenu";
import { Merchant } from "@/dto/merchant";
import { Service } from "@/dto/service";
import { isEmpty, isNil } from "lodash";
import { isBusinessOpen } from "./isBusinessOpenUtil";
import { TFunction } from "@/types";
import { OrderType, ServiceType, daysOfWeek } from "@/constants/enum";
import {ScheduleDateType, ScheduledDateCalendarOptions} from "@/dto/scheduledTime";
import { Hours } from "@/dto/hours";
import { TIME_CONSTANTS } from "./constants";
import {
  BusyModeFields,
  getEffectiveLeadTime,
  toBusyModeFields,
} from "./busyModeLeadTime";

type ScheduleTimestampInput = string | number | Date | null;

const DEFAULT_SCHEDULING_LOCALE = "en-US";

export interface SchedulingContext {
  now: DateTime;
  timezone: string;
  locale: string;
  hours: Hours | undefined;
  leadTime: number;
  deliveryDelay: number;
  busyModeFields?: BusyModeFields;
  orderType?: OrderType;
  daypartMenu?: DayPart | null;
}

interface CreateSchedulingContextParams {
  merchant: Merchant;
  locale?: string;
  orderType?: OrderType;
  orderEstimates?: {
    deliveryTime?: ScheduleTimestampInput;
    pickupTime?: ScheduleTimestampInput;
  };
  daypartMenu?: DayPart | null;
  hoursOverride?: Hours | null;
  now?: DateTime;
}

export const createSchedulingContext = ({
  merchant,
  locale,
  orderType,
  orderEstimates,
  daypartMenu,
  hoursOverride,
  now,
}: CreateSchedulingContextParams): SchedulingContext => {
  const { timezone } = merchant;
  const { deliveryTime, pickupTime } = orderEstimates ?? {};

  return {
    now: (now ?? DateTime.now()).setZone(timezone),
    timezone,
    locale: locale ?? DEFAULT_SCHEDULING_LOCALE,
    hours: resolveMerchantSchedulingHours(merchant, hoursOverride),
    leadTime: merchant.isRestaurant ? (merchant.leadTime ?? 0) : 0,
    deliveryDelay: getDeliveryDelayMinutes({
      orderType,
      deliveryTime,
      pickupTime,
    }),
    busyModeFields: merchant.isRestaurant ? toBusyModeFields(merchant) : undefined,
    orderType,
    daypartMenu,
  };
};

export const getDeliveryDelayMinutes = ({
  orderType,
  deliveryTime,
  pickupTime,
}: {
  orderType?: OrderType;
  deliveryTime?: ScheduleTimestampInput;
  pickupTime?: ScheduleTimestampInput;
}) => {
  if (orderType !== OrderType.DELIVERY || !deliveryTime || !pickupTime) {
    return 0;
  }

  const deliveryTimeDate = new Date(deliveryTime).getTime();
  const pickUpTimeDate = new Date(pickupTime).getTime();

  return Math.round(
    (deliveryTimeDate - pickUpTimeDate) /
      TIME_CONSTANTS.MILLISECONDS_PER_SECOND /
      TIME_CONSTANTS.MINUTES_PER_HOUR
  );
};

export const isCandidateEligibleForScheduling = ({
  candidate,
  now,
  startTime,
  leadTime,
  deliveryDelay,
  busyModeFields,
}: {
  candidate: DateTime;
  now: DateTime;
  startTime: DateTime;
  leadTime: number;
  deliveryDelay: number;
  busyModeFields?: BusyModeFields;
}) => {
  const baselineStart =
    candidate.hasSame(now, "day") && startTime < now ? now : startTime;
  // Per product: busy mode is evaluated at the baseline (now, or opening time when not yet open),
  // not at the candidate slot, so scheduled slots never undercut the ASAP quote while busy.
  // getEffectiveLeadTime already returns `leadTime` when busy mode is absent, so no guard is needed here.
  const candidateLeadTime = getEffectiveLeadTime(
    leadTime,
    busyModeFields,
    baselineStart.toMillis()
  );

  const threshold = roundToNext15Minutes(
    baselineStart.plus({ minutes: candidateLeadTime + deliveryDelay })
  );

  return candidate >= threshold;
};

export const isWithinBusinessHours = (
  dateTime: DateTime,
  businessHours: any,
  timezone: string,
  daypartMenu: DayPart | null = null
): boolean => {
  const zonedTime = dateTime.setZone(timezone);
  const day = zonedTime.toFormat("cccc").toLowerCase();

  const businessHoursForDay = businessHours?.[day] ?? [];
  const { days: dpmDays, time: dpmTime } = daypartMenu || {};

  // If there are no business hours for the day, the business is closed.
  if (businessHoursForDay.length === 0) {
    return false;
  }

  let effectiveHours: { start: number; end: number }[] = [];

  const isDpmAvailableToday =
    dpmDays?.map((d) => d.toLowerCase()).includes(day) && dpmTime;

  if (isDpmAvailableToday) {
    // Daypart menu exists for today, so find the intersection with business hours.
    const menuEndTime = dpmTime.endTime;
    effectiveHours = businessHoursForDay
      .map((slot: any) => ({
        start: Math.max(slot.start, dpmTime.startTime),
        end: Math.min(slot.end, menuEndTime),
      }))
      .filter((slot: any) => slot.start < slot.end); // Keep only valid (non-empty) intersections
  } else if (!daypartMenu) {
    // No daypart menu was passed in, so just use the general business hours.
    effectiveHours = businessHoursForDay;
  }
  // If a daypart menu was passed but not for today, effectiveHours remains empty, and the function will return false.

  return effectiveHours.some((timeSlot) => {
    const start = DateTime.fromFormat(
      String(timeSlot.start).padStart(4, "0"),
      "HHmm",
      {
        zone: timezone,
      }
    ).set({
      year: zonedTime.year,
      month: zonedTime.month,
      day: zonedTime.day,
    });

    const endRaw = DateTime.fromFormat(
      String(timeSlot.end).padStart(4, "0"),
      "HHmm",
      {
        zone: timezone,
      }
    ).set({
      year: zonedTime.year,
      month: zonedTime.month,
      day: zonedTime.day,
    });
    const end =
      Number(timeSlot.end) === 2400 ? endRaw.plus({ days: 1 }) : endRaw;

    return (
      start.isValid && end.isValid && zonedTime >= start && zonedTime <= end
    );
  });
};

interface SchedulableDay {
  /** Offset in days from the scan clock; 0 is today. */
  offset: number;
  /** The day being evaluated, in the merchant timezone, retaining the scan clock's time of day. */
  date: DateTime;
  /** Configured (optionally daypart-narrowed) windows for that weekday; empty means closed. */
  businessHoursForDay: TimeRange[];
  /** Slots surviving lead time, busy mode, delivery delay and the clock. Empty means unschedulable. */
  timeOptions: TimeOption[];
}

interface SchedulableDayScan {
  /** The single clock reading used for the whole scan, in the merchant timezone. */
  now: DateTime;
  days: SchedulableDay[];
}

/**
 * Single source of truth for "can this merchant take an order on day N?".
 *
 * Both the date dropdown (`getSchedulableDateOptions`) and the Advanced Ordering calendar
 * (`getSchedulableDateCalendarOptions`) are projections of this one scan, so the date surface
 * can never disagree with the time surface. A day is schedulable iff it yields at least one
 * time option under the same lead time, busy mode, delivery delay and clock.
 *
 * Reads the system clock exactly once, or not at all when `context.now` is injected.
 */
const scanSchedulableDays = ({
  context,
  scheduleDateRange,
}: {
  context: SchedulingContext;
  scheduleDateRange: number;
}): SchedulableDayScan => {
  const { timezone, hours, daypartMenu } = context;
  // `context.now` is guaranteed by `createSchedulingContext`; re-pin to the timezone so the
  // whole scan is evaluated against one clock reading.
  const now = context.now.setZone(timezone);
  const scanContext: SchedulingContext = { ...context, now };

  const days: SchedulableDay[] = [];

  for (let i = 0; i <= scheduleDateRange; i++) {
    const date = now.plus({ days: i });
    const dayName = daysOfWeek[date.weekday % 7] as keyof Hours;
    const businessHoursForDay = getBusinessHoursOfDay(
      hours,
      dayName,
      daypartMenu
    );

    // Closed that weekday: recorded so the calendar can tell "closed" from "no reachable slot".
    if (!businessHoursForDay || businessHoursForDay.length === 0) {
      days.push({ offset: i, date, businessHoursForDay: [], timeOptions: [] });
      continue;
    }

    days.push({
      offset: i,
      date,
      businessHoursForDay,
      timeOptions: getAvailableTimeOptions({
        dateObject: date.toJSDate(),
        timeRange: businessHoursForDay,
        context: scanContext,
      }),
    });
  }

  return { now, days };
};

/**
 * A utility function that calculates a list of valid, schedulable date options
 * based on the merchant's business hours, timezone constraints, and order lead time.
 *
 * @param props - Configuration properties for determining date availability.
 * @returns {ScheduleDateType[]} An array of valid schedulable dates with localized display values.
 */
export const getSchedulableDateOptions = (
  {
    context,
    scheduleDateRange,
    t,
  }: {
    context: SchedulingContext;
    scheduleDateRange: number;
    t: TFunction;
  }
): ScheduleDateType[] => {
  const { now, days } = scanSchedulableDays({ context, scheduleDateRange });

  return days
    .filter((day) => day.timeOptions.length > 0)
    .map((day) => ({
      displayValue: formatDate(day.date, t, now),
      dateObject: day.date.toJSDate(),
    }));
};

/** Maps the `Hours` weekday keys onto the abbreviations the Sprout calendar expects. */
const CALENDAR_DAY_KEYS: Record<
  keyof Hours,
  "Sun" | "Mon" | "Tue" | "Wed" | "Thu" | "Fri" | "Sat"
> = {
  sunday: "Sun",
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
};

/**
 * A utility function that calculates days off, days of the week off, and boundaries for scheduling dates
 * based on the merchant's business hours, timezone constraints, and order lead time.
 *
 * Shares `scanSchedulableDays` with `getSchedulableDateOptions`, so a date greyed out here is
 * exactly a date whose time dropdown would render empty.
 *
 * @param props - Configuration properties for determining date availability.
 * @returns
 */
export const getSchedulableDateCalendarOptions = ({
  context,
  scheduleDateRange,
}: {
  context: SchedulingContext;
  scheduleDateRange: number;
}): ScheduledDateCalendarOptions => {
  const { now, days } = scanSchedulableDays({ context, scheduleDateRange });
  const startOfToday = now.startOf("day");
  // A negative/NaN scheduleDateRange (disabled scheduling or maxDays = 0/undefined) scans zero
  // days; fall back to today instead of dereferencing a last day that was never scanned.
  const lastDay = days.at(-1)?.date ?? now;
  const endOfRange = lastDay.endOf("day");

  // Weekdays the merchant never opens are disabled recurrently rather than date by date.
  const disableRecurringDays: DayAbbreviationType = [];
  const { hours: businessHours } = context;
  if (businessHours) {
    for (const day of Object.keys(businessHours)) {
      if (businessHours[day as keyof Hours].length === 0) {
        disableRecurringDays.push(CALENDAR_DAY_KEYS[day as keyof Hours]);
      }
    }
  }

  // Disable any in-range day with no reachable slot — whether exhausted by lead time / busy mode /
  // clock, or narrowed to empty by the daypart menu (e.g. a Monday-only menu closes Tue-Sun even
  // though the merchant opens then). Don't gate on businessHoursForDay: daypart-closed days are open
  // on the raw schedule yet still need greying out. Never-open weekdays are handled recurrently
  // above; re-listing them here is harmless.
  const disableDates = days
    .filter((day) => day.timeOptions.length === 0)
    .map((day) => day.date.toFormat("yyyy-MM-dd"));

  return {
    disableDates,
    disableRecurringDays,
    dateBoundaries: {
      startTimestamp: startOfToday.toMillis(),
      endTimestamp: endOfRange.toMillis(),
    },
  };
};

/**
 * Format a date to match how it's displayed in the schedule order UI.
 *
 * @param date The date to format.
 * @param t Translator supplying the "today"/"tomorrow" labels.
 * @param now Clock reference. `scanSchedulableDays` injects its own reading so labels stay
 *            consistent with the dates it produced; defaults to the system clock.
 * @returns An internationalized string for today, tomorrow, or the selected date.
 */
export const formatDate = (
  date: Date | DateTime,
  t: TFunction,
  now: DateTime = DateTime.now()
): string => {
  const dateTime = date instanceof DateTime ? date : DateTime.fromJSDate(date);

  if (dateTime.hasSame(now, 'day')) {
    return t('today');
  }

  if (dateTime.hasSame(now.plus({ days: 1 }), 'day')) {
    return t('tomorrowDate', { formattedDate: dateTime.toFormat('MMM d') });
  }

  return dateTime.toFormat('MMM d');
}

export const getBusinessHoursOfDay = (
  businessHours: any,
  dayName: string,
  daypartMenu?: DayPart | null
) => {
  const businessHoursForDay = businessHours?.[dayName] || [];
  if (!daypartMenu) return businessHoursForDay;

  const { days: dpmDays, time: dpmTime } = daypartMenu || {};

  if (!dpmDays?.map((d) => d.toLowerCase()).includes(dayName) || !dpmTime)
    return [];

  const daypartMenuHours = getDayPartMenuHours(businessHoursForDay, dpmTime);

  return daypartMenuHours.map((slot: any) => ({
    start: slot.start.toString(),
    end: slot.end.toString(),
  }));
};

export const getDayPartMenuHours = (businessHoursForDay: any, dpmTime: any) => {
  const { startTime: start, endTime: end } = dpmTime;

  // Check if the menu time spans across midnight (end time is smaller than start time)
  if (end <= start) {
    // Handle times that span to next day (e.g., 22:00 to 02:00)
    const menuHours = businessHoursForDay.flatMap((slot: any) => {
      const results = [];

      // Check intersection with the time from start to end of day (2400)
      if (slot.end >= start) {
        const startTime = Math.max(slot.start, start);
        const endTime = slot.end;
        if (startTime < endTime) {
          results.push({ start: startTime, end: endTime });
        }
      }

      // Check intersection with the time from start of day (0000) to end
      if (slot.start <= end) {
        const startTime = slot.start;
        const endTime = Math.min(slot.end, end);
        if (startTime < endTime) {
          results.push({ start: startTime, end: endTime });
        }
      }

      return results;
    });

    return menuHours;
  } else {
    // Normal case where end time is after start time (same day)
    const menuHours = businessHoursForDay.map((slot: any) => {
      const startTime = Math.max(slot.start, start);
      const endTime = Math.min(slot.end, end);
      return { start: startTime, end: endTime };
    });

    return menuHours.filter((slot: any) => slot.start < slot.end);
  }
};

export const canAcceptASAPOrders = (
  merchant: Merchant,
  isActiveDayPartMenu: boolean
) => {
  if (!isActiveDayPartMenu) return false;

  const now = new Date();
  const currentDateTime = DateTime.fromJSDate(now, {
    zone: merchant.timezone,
  });
  const merchantService = merchant.services.find(
    (s: Service) =>
      s.type === ServiceType.PICKUP || s.type === ServiceType.WEBSITE
  );
  const businessHours = merchantService?.hours;

  const isNowWithinBusinessHours = isWithinBusinessHours(
    currentDateTime,
    businessHours,
    merchant.timezone
  );

  return isNowWithinBusinessHours;
};

export const isOrderSchedulingEnabled = (merchant: Merchant) => {
  if (!merchant || isEmpty(merchant.scheduledOrdering)) return false;

  if (!merchant.scheduledOrdering.enabled) return false;

  if (
    merchant.scheduledOrdering.maxDays === 0 ||
    isNil(merchant.scheduledOrdering.maxDays)
  )
    return false;

  return true;
};

export const canMerchantAcceptOrders = (
  merchant: Merchant,
  isDineIn: boolean,
  dayPartMenu: DayPart | null,
  t: TFunction
) => {
  const isActiveDayPartMenu = dayPartMenu?.activeNow ?? true;
  //isBusinessOpen checks if busines is accepting online orders
  const isOpen = isBusinessOpen(merchant, isDineIn, isActiveDayPartMenu);

  //If not restaurant merchante, return if business open or not
  if (!merchant.isRestaurant) return isOpen;

  //If business is not open return false
  if (!isOpen) return false;

  //check if scheduling order is enabled for merchant
  const isSchedulingEnabled = isOrderSchedulingEnabled(merchant);

  //check if ASAP orders can be placed for current menu based on hours and current time
  const acceptingASAPOrders = canAcceptASAPOrders(
    merchant,
    isActiveDayPartMenu
  );
  //If orderscheduling is disabled, then return if asap orders accepted or not
  if (!isSchedulingEnabled) {
    return acceptingASAPOrders;
  }

  //If order scheduling is enabled then get schedulable dates. Either ASAP orders should be acceptable or should be able to schedule order based on menu availability and business hours
  const context = createSchedulingContext({
    merchant,
    orderType: OrderType.PICKUP,
    daypartMenu: dayPartMenu,
  });
  const schedulableDateOptions = getSchedulableDateOptions({
    context,
    scheduleDateRange: merchant?.scheduledOrdering.maxDays - 1,
    t,
  });

  return acceptingASAPOrders || schedulableDateOptions?.length > 0;
};

export const getMerchantBusinessHours = (merchant: Merchant) => {
  const merchantService = merchant.services.find(
    (s: Service) =>
      s.type === ServiceType.PICKUP || s.type === ServiceType.WEBSITE
  );

  return merchantService?.hours;
};

export const resolveMerchantSchedulingHours = (
  merchant: Merchant | undefined,
  hoursOverride?: Hours | null
): Hours | undefined => {
  if (hoursOverride === null) return undefined;
  if (hoursOverride !== undefined) return hoursOverride;
  if (!merchant) return undefined;

  return merchant.isRestaurant
    ? getMerchantBusinessHours(merchant)
    : merchant.businessHours?.hours;
};

/**
 * Determines if the selected scheduled pickup time is no longer viable
 * because it is in the past or falls within the merchant's required lead time.
 *
 * @param scheduledTimeState - The user-selected scheduled time as an ISO string or Date object.
 * @param timezone - The IANA timezone string of the merchant (e.g., "America/New_York").
 * @param leadTimeMinutes - The merchant's required preparation buffer in minutes.
 * @returns boolean - True if the scheduled time is before (now + leadTime), false otherwise.
 */
export const isPastSelectedPickupTime = (
  scheduledTimeState: Date | string,
  timezone: string,
  leadTimeMinutes: number = 0
): boolean => {
  if (!scheduledTimeState) {
    return false;
  }

  let scheduledDateTime: DateTime;

  if (scheduledTimeState instanceof Date) {
    scheduledDateTime =
      DateTime.fromJSDate(scheduledTimeState).setZone(timezone);
  } else {
    scheduledDateTime = DateTime.fromISO(scheduledTimeState).setZone(timezone);
  }

  // Safety check in case of an invalid date string/object
  if (!scheduledDateTime.isValid) {
    return false;
  }

  // Calculate the cutoff threshold: current time + lead time buffer, if any
  const cutoffTime = DateTime.now()
    .setZone(timezone)
    .plus({ minutes: leadTimeMinutes });

  return scheduledDateTime < cutoffTime;
};

interface TimeRange {
  start: string;
  end: string;
}

interface AvailableTimeOptionsParams {
  dateObject: Date | null | undefined;
  timeRange: TimeRange[];
  context: SchedulingContext;
}

interface TimeOption {
  displayValue: string;
  timeObject: Date;
}

const toValidDate = (value: ScheduleTimestampInput | undefined) => {
  if (!value) {
    return null;
  }

  const parsed = value instanceof Date ? value : new Date(value);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Generates a list of available pickup/delivery 15-minute time slots based on business hours,
 * required lead times, and the merchant's timezone.
 */
export const getAvailableTimeOptions = ({
  dateObject,
  timeRange,
  context,
}: AvailableTimeOptionsParams): TimeOption[] => {
  if (!dateObject || !timeRange || timeRange.length === 0) {
    return [];
  }

  const {
    timezone,
    locale,
    leadTime,
    deliveryDelay,
    busyModeFields,
    now: contextNow,
  } = context;

  // `context.now` is guaranteed by `createSchedulingContext`.
  const now = contextNow.setZone(timezone).setLocale(locale);
  const currentDayInMerchantZone = DateTime.fromJSDate(dateObject, {
    zone: timezone,
  });

  // If the selected day is in the past, no slots are available
  if (currentDayInMerchantZone.startOf("day") < now.startOf("day")) {
    return [];
  }

  const options: TimeOption[] = [];

  timeRange.forEach(({ start, end }) => {
    const startTime = HHMMtoDate(
      String(start).padStart(4, "0"),
      timezone,
      dateObject
    );
    const rawEndTime = HHMMtoDate(
      String(end).padStart(4, "0"),
      timezone,
      dateObject
    );
    // Keep legacy behavior from the pre-refactor getAvailableTimeOptions flow:
    // a selected schedule date only surfaces slots on that same calendar day, so overnight ranges are skipped.
    if (rawEndTime < startTime) {
      return;
    }
    const endTime = rawEndTime;

    const firstCandidate = roundToNext15Minutes(startTime);

    for (
      let time = firstCandidate;
      time <= endTime;
      time = time.plus({ minutes: 15 })
    ) {
      if (!time.hasSame(currentDayInMerchantZone, "day")) {
        continue;
      }

      if (
        !isCandidateEligibleForScheduling({
          candidate: time,
          now,
          startTime,
          leadTime,
          deliveryDelay,
          busyModeFields,
        })
      ) {
        continue;
      }

      options.push({
        displayValue: time
          .setLocale(locale)
          .toLocaleString(DateTime.TIME_SIMPLE),
        timeObject: time.toJSDate(),
      });
    }
  });

  return options;
};

export const getFirstAvailableTimeOption = (
  params: AvailableTimeOptionsParams
): TimeOption | null => {
  const [firstSlot] = getAvailableTimeOptions(params);

  return firstSlot ?? null;
};

/**
 * Resolves the schedulable time slots for a single calendar date, deriving that day's
 * business-hours window from the scheduling context. Shared by the scheduled dropdown
 * hook and stale-time availability checks so they can never diverge.
 */
export const getTimeOptionsForDate = ({
  dateObject,
  context,
}: {
  dateObject: Date;
  context: SchedulingContext;
}): TimeOption[] => {
  const dayIndex = DateTime.fromJSDate(dateObject, {
    zone: context.timezone,
  }).weekday;
  const dayName = daysOfWeek[dayIndex % 7];
  const timeRange = getBusinessHoursOfDay(
    context.hours,
    dayName,
    context.daypartMenu
  );

  return getAvailableTimeOptions({
    dateObject,
    timeRange,
    context,
  });
};

export const isScheduledTimeStillAvailable = ({
  context,
  scheduleDate,
  scheduledTime,
}: {
  context: SchedulingContext;
  scheduleDate?: ScheduleTimestampInput;
  scheduledTime?: ScheduleTimestampInput;
}) => {
  const normalizedDate = toValidDate(scheduleDate);
  const normalizedScheduledTime = toValidDate(scheduledTime);

  if (!normalizedDate || !normalizedScheduledTime) {
    return false;
  }

  // Availability depends on order type (delivery delay, service hours). Without it the
  // context carries a 0 delivery delay and cannot be trusted, matching the pre-refactor
  // guard that bailed out when merchant/orderType were missing.
  if (!context.orderType) {
    return false;
  }

  const scheduledDateTime = DateTime.fromJSDate(normalizedScheduledTime, {
    zone: context.timezone,
  });

  return getTimeOptionsForDate({
    dateObject: normalizedDate,
    context,
  }).some((option) =>
    DateTime.fromJSDate(option.timeObject, { zone: context.timezone }).hasSame(
      scheduledDateTime,
      "minute"
    )
  );
};

